/* =============================================================================
   VIEWS / ADMIN / DAY-CLOSE-ZBON-ACTION — Z-Bon-Action-Modal + Persistierung
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5b Cut 3/4
   ----------------------------------------------------------------------------
   Z-Bon-Action-Modal (Versenden, Drucken, Archivieren) + Standalone-HTML-
   Generator + zbon_archive-Upsert. Vereinheitlicht den Z-Bon-Workflow mit
   dem S22-Bon-Flow für Einzelbestellungen (Phase 29 / S29).

   Cross-Module:
     - loadAndRenderDayClose via window.loadAndRenderDayClose (verhindert
       Circular-Import zwischen render.js und zbon-action.js)
============================================================================ */

import { htmlEscape, todayISO, yesterdayISO, showToast } from '../../utils/helpers.js';
import {
  renderDayCloseReceipt, computeDayCloseReceipt, printReceiptHtml,
  renderDayCloseReceiptFresh, computeDayCloseReceiptFresh
} from './receipt.js';
import { sendZBonReceiptEmail, _buildReceiptPdfAttachment } from '../../core/email-templates.js';
import { _state, getState, fmtDateDE, centsToEur } from './day-close-state.js';

/* ============================================================================
   PHASE 29 — Z-Bon-Beleg-Aktion-Modal + Z-Bon-Archiv (Session 27 / S29 DB)
============================================================================ */

/**
 * S29 KRITISCH-Fix (Memory-Lesson gg-db-for-critical-data):
 * loadZBonArchive liest jetzt aus dem In-Memory-Cache des Storage-Adapters
 * (`_state.zbonArchive`), der beim Boot via `App.storage.zbonArchive.hydrate()`
 * aus der `zbon_archive`-Supabase-Tabelle befüllt wird. Kein localStorage
 * mehr — DB ist Single-Source-of-Truth.
 */
export function loadZBonArchive() {
  try {
    if (window.App && window.App.storage && window.App.storage.zbonArchive
        && typeof window.App.storage.zbonArchive.getCache === 'function') {
      const obj = window.App.storage.zbonArchive.getCache();
      return (obj && typeof obj === 'object') ? obj : {};
    }
  } catch (e) {
    console.warn('[loadZBonArchive] state read failed:', e);
  }
  return {};
}

/**
 * @deprecated S29 — Z-Bon-Archiv ist DB-only. Stub für Tests-Backward-
 *   Compat. Persistierung läuft über `App.storage.zbonArchive.upsert`.
 */
export function saveZBonArchive(_obj) { /* DB-only — no localStorage write */ }

/** Liefert die Z-Bon-Zusammenfassung für einen Stichtag, formatiert für UI/Mail.
 *  Sync-Cache-Variante — nur für Tests/Backward-Compat. Der echte Z-Bon-Pfad
 *  nutzt `buildZBonPayloadFresh` (DB-Single-Source). */
export function buildZBonPayload(dateIso) {
  const data = computeDayCloseReceipt(dateIso);
  return { data, empty: _isZBonEmpty(data) };
}

/**
 * S92 — DB-autoritative Z-Bon-Zusammenfassung (Single-Source-of-Truth).
 * Liest die Tagesdaten frisch aus der DB (`computeDayCloseReceiptFresh` →
 * `listForDayClose`), NICHT aus dem `_state.orders`-Cache. Pflicht für jeden
 * Pfad, der den Z-Bon versendet/druckt/archiviert oder das Aktions-Modal öffnet.
 */
export async function buildZBonPayloadFresh(dateIso) {
  const data = await computeDayCloseReceiptFresh(dateIso);
  return { data, empty: _isZBonEmpty(data) };
}

function _isZBonEmpty(data) {
  return data.count === 0 && data.countCancelled === 0
         && data.countNoShow === 0 && data.countCorrections === 0
         && (data.countSchwund || 0) === 0;
}

/** Baut ein Standalone-HTML-Dokument für den Z-Bon (analog zum Bon-Standalone
 *  in orders.js). CSS deckt die Receipt-Klassen + Day-Close-Sektionen ab. */
export function buildStandaloneZBonHtml(dateIso) {
  return _wrapStandaloneZBonHtml(dateIso, renderDayCloseReceipt(dateIso));
}

/**
 * S92 — DB-autoritative Standalone-HTML-Variante. Rendert den Z-Bon-Beleg aus
 * frischen DB-Daten (`renderDayCloseReceiptFresh`). Wird vom Versand-/Druck-/
 * Archiv-Pfad genutzt, damit der persistierte/versendete Beleg die DB als
 * Single-Source spiegelt — auch für historische Re-Prints außerhalb des
 * 30-Tage-Cache-Fensters.
 */
export async function buildStandaloneZBonHtmlFresh(dateIso) {
  const body = await renderDayCloseReceiptFresh(dateIso);
  return _wrapStandaloneZBonHtml(dateIso, body);
}

function _wrapStandaloneZBonHtml(dateIso, body) {
  const title = `Z-Bon ${fmtDateDE(dateIso)}`;
  const css = `
    body { font-family: system-ui, -apple-system, sans-serif; background: #f4f1ea; margin: 0; padding: 24px; color: #1a2622; }
    .receipt { background: #fff; max-width: 540px; margin: 0 auto; padding: 28px 32px; border-radius: 14px; box-shadow: 0 6px 24px rgba(0,0,0,0.08); }
    .receipt-header { text-align: center; border-bottom: 2px solid #1a2622; padding-bottom: 14px; margin-bottom: 18px; }
    /* Phase 31 (S29 Funktionstest-Fix): Logo-Begrenzung muss auch im Standalone-CSS
       stehen, sonst rendert das Bild in nativer Auflösung (~1024px). */
    .receipt-logo { display: block; max-width: 80px; max-height: 60px; width: auto; height: auto; margin: 0 auto 8px auto; object-fit: contain; }
    .receipt-brand { font-family: cursive; font-size: 1.9rem; color: #2f6b3a; }
    .receipt-legal-name { font-weight: 600; margin-top: 4px; }
    .receipt-addr, .receipt-meta-row { font-size: 0.84rem; opacity: 0.78; }
    .receipt-tax-id { font-size: 0.78rem; opacity: 0.78; margin-top: 4px; }
    .receipt-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; margin: 14px 0; font-size: 0.86rem; }
    .receipt-label { display: block; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7; }
    .receipt-value { font-weight: 600; }
    .receipt-overview { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 14px 0; }
    .receipt-overview-card { background: rgba(47,107,58,0.06); border-left: 3px solid #2f6b3a; padding: 10px 12px; border-radius: 4px; }
    .receipt-overview-card-correction { background: rgba(180,50,50,0.05); border-left-color: #b43232; }
    .receipt-overview-card-muted { background: rgba(0,0,0,0.04); border-left-color: rgba(0,0,0,0.25); }
    .receipt-overview-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7; }
    .receipt-overview-value { font-size: 1.4rem; font-weight: 700; margin-top: 2px; }
    .receipt-overview-sub { font-size: 0.82rem; opacity: 0.78; }
    .receipt-tax-table { width: 100%; font-size: 0.86rem; margin-top: 8px; }
    .receipt-tax-table td { padding: 2px 4px; }
    .receipt-tax-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .receipt-total-row { display: flex; justify-content: space-between; font-weight: 700; font-size: 1.05rem; margin-top: 8px; padding-top: 8px; border-top: 2px solid #1a2622; }
    .receipt-payment { margin-top: 14px; }
    .receipt-payment h4 { margin: 0 0 4px; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7; }
    .receipt-payment-table { width: 100%; font-size: 0.88rem; }
    .receipt-payment-table td { padding: 2px 4px; }
    .receipt-payment-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .receipt-payment-unpaid td { opacity: 0.7; font-style: italic; }
    /* S136 Cut C — Schwund/Warenverlust getrennt vom Umsatz/TSE-Teil. */
    .receipt-schwund { border-top: 1px dashed #c9bfa9; padding-top: 10px; }
    .receipt-tse { background: rgba(0,0,0,0.04); padding: 10px 12px; border-radius: 4px; margin-top: 12px; font-size: 0.82rem; }
    .receipt-tse-pending { background: rgba(180,50,50,0.04); border: 1px dashed #b43232; color: #8b2828; }
    .receipt-tse-badge { display: inline-block; background: #3c8c50; color: #fff; padding: 1px 5px; border-radius: 3px; font-size: 0.66rem; margin-right: 4px; }
    .receipt-tse-pending .receipt-tse-badge { background: #b43232; }
    .receipt-footer { margin-top: 14px; font-size: 0.78rem; opacity: 0.78; }
    .receipt-disclaimer { margin-top: 6px; }
    .receipt-print-meta { text-align: right; font-size: 0.7rem; opacity: 0.6; margin-top: 6px; }
    .receipt-empty { text-align: center; padding: 20px; opacity: 0.6; }
    @media print { body { background: #fff; } .receipt { box-shadow: none; } }
  `;
  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${htmlEscape(title)}</title><style>${css}</style></head><body>${body}</body></html>`;
}

/** Modul-private Referenz auf den aktuell im Modal sichtbaren Z-Bon. */
let _currentZBon = null;

/**
 * S93-B — GoBD-§147-AO garantierte Z-Bon-Archivierung (idempotent, INSERT-once),
 * analog zum Kassenbon-Pfad `_ensureBonArchived`. Archiviert NUR **abgeschlossene
 * Tage** (`dateIso < heute`, Europe/Berlin) — der laufende Tag ist noch nicht fertig
 * und würde sonst als unvollständiger Snapshot abgelegt (+ späteres UPDATE liefe in
 * die zbon_archive-RLS, die UPDATE nur owner/admin erlaubt).
 *
 * Idempotenz über den In-Memory-Cache: ist der Tag schon archiviert, wird NICHT neu
 * geschrieben (sonst Overwrite von `archived_at`). Best-effort — wirft nie (RLS-
 * Blocks o.ä. landen als console.warn). Quelle = frische DB-Daten (Single-Source).
 *
 * @param {string} dateIso Buchungstag
 * @param {object} [data]  bereits frisch geladene Z-Bon-Daten (optional, spart Read)
 * @param {string} [html]  bereits frisch gebautes Standalone-HTML (optional)
 * @returns {Promise<{archived:boolean, alreadyArchived:boolean}>}
 */
export async function ensureZBonArchivedForDay(dateIso, data, html) {
  try {
    if (!dateIso || dateIso >= todayISO()) {
      return { archived: false, alreadyArchived: false }; // laufender/künftiger Tag
    }
    const cache = loadZBonArchive();
    if (cache && cache[dateIso] && cache[dateIso].archivedAt) {
      return { archived: false, alreadyArchived: true };
    }
    const d = data || await computeDayCloseReceiptFresh(dateIso);
    if (_isZBonEmpty(d)) return { archived: false, alreadyArchived: false }; // kein Umsatz
    const h = html || await buildStandaloneZBonHtmlFresh(dateIso);
    await upsertZBonArchiveEntry(dateIso, d, h, (e) => {
      if (!e.archivedAt) e.archivedAt = new Date().toISOString();
    });
    return { archived: true, alreadyArchived: false };
  } catch (e) {
    console.warn('[ensureZBonArchivedForDay] best-effort-Archivierung fehlgeschlagen:', e);
    return { archived: false, alreadyArchived: false };
  }
}

/**
 * S93-B — Day-Start-Flow: prüft beim ersten Cockpit-Aufruf, ob für den **Vortag**
 * bereits ein Z-Bon existiert. Falls nicht (und der Vortag Umsatz hatte), wird er
 * automatisch archiviert (wie die Kassenbons) und das Aktions-Modal öffnet sich, damit
 * der Manager direkt versenden/drucken kann. Nur für owner/manager; idempotent (poppt
 * nicht erneut, wenn schon archiviert). Best-effort — bricht den Boot nie.
 */
let _autoArchiveCheckedForDate = null;

/** Test-Hook: setzt den Ein-Mal-Guard zurück (sonst kleben Tests am Modul-State). */
export function _resetAutoArchiveGuardForTests() { _autoArchiveCheckedForDate = null; }

/**
 * S136 Cut B — Z-Bon ist jetzt **nur manuell** (Jonas-Entscheid).
 *
 * Der frühere Auto-Pfad hat den Vortags-Z-Bon beim Cockpit-Start automatisch
 * erstellt + archiviert + das Aktions-Modal aufgepoppt (S93-B). Das ist
 * abgeschaltet: kein automatischer Tagesabschluss mehr. Der manuelle Pfad
 * (`openZBonReceiptActionModal` / `zBonActionArchive` über den Tagesabschluss-Tab)
 * bleibt unverändert.
 *
 * Die Funktion bleibt als **No-Op** erhalten (statt sie zu löschen): die
 * window-Bridge in `day-close.js` + die Test-Suite referenzieren das Symbol;
 * ein inerter Body garantiert zusätzlich, dass kein verbleibender Aufrufer
 * mehr einen ungewollten Auto-Abschluss auslöst.
 */
export async function maybeAutoArchiveYesterdayZBon() {
  /* no-op — Z-Bon nur manuell (S136 Cut B) */
}

export async function openZBonReceiptActionModal(dateIsoArg, opts) {
  const dateIso = dateIsoArg || _state.date || todayISO();
  // S92: DB-autoritativ — Modal-Inhalt aus frischen DB-Daten, nicht aus Cache.
  const { data, empty } = (opts && opts.data)
    ? { data: opts.data, empty: _isZBonEmpty(opts.data) }
    : await buildZBonPayloadFresh(dateIso);
  if (empty) {
    showToast('Für diesen Tag liegt kein Tagesumsatz vor — Z-Bon nicht ausgegeben.', 'info');
    return;
  }
  // S93-B: Abgeschlossene Tage werden beim Öffnen automatisch archiviert (idempotent,
  // GoBD §147 AO) — wie die Kassenbons. Der manuelle „archivieren"-Button entfällt.
  const isPastDay = dateIso < todayISO();
  if (isPastDay) await ensureZBonArchivedForDay(dateIso, data);
  _currentZBon = { dateIso, data };

  const settings = (getState().settings || {});
  const recipient = (settings.accountantEmail || settings.restaurantEmail || '').trim();
  const mailDisabled = !recipient;
  const mailHint = mailDisabled
    ? '<div class="hint" style="margin-top:6px;color:var(--danger);">Keine Empfänger-Mail hinterlegt (Settings → Steuerberater-Mail / Restaurant-Mail).</div>'
    : `<div class="hint" style="margin-top:6px;">Empfänger: ${htmlEscape(recipient)}</div>`;

  const zNr = htmlEscape(data.zBonNumber || '');
  const dateLabel = htmlEscape(fmtDateDE(dateIso));
  const grossLabel = centsToEur(data.grossCents);

  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay receipt-action-overlay zbon-action-overlay';
  modal.id = 'zbon-action-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal receipt-action-modal" onclick="event.stopPropagation()">
      <header class="order-modal-header">
        <div>
          <div class="order-modal-eyebrow" style="color:var(--green);">Tagesabschluss</div>
          <h2 class="order-modal-title">Z-Bon ${zNr}</h2>
          <div class="hint" style="margin-top:4px;">${dateLabel} · ${data.count} Bestellungen · <strong>${grossLabel}</strong></div>
        </div>
        <button class="order-modal-close" onclick="closeZBonReceiptActionModal()" aria-label="Schließen">×</button>
      </header>

      <div class="order-modal-body">
        ${isPastDay ? `
        <div class="receipt-action-archive-note" style="background:rgba(47,107,58,0.07);border-left:3px solid var(--green);padding:10px 12px;border-radius:4px;margin-bottom:14px;font-size:0.86rem;">
          🗄️ <strong>Automatisch archiviert</strong> — sichtbar in Auswertung → Archiv → Z-Bons.
          Bitte jetzt an den Steuerberater <strong>versenden</strong> oder <strong>drucken</strong>.
        </div>` : `
        <p class="hint" style="margin-bottom:14px;">
          Der laufende Tag wird automatisch archiviert, sobald er abgeschlossen ist
          (beim ersten Cockpit-Aufruf des Folgetags). Du kannst ihn bereits jetzt
          versenden oder drucken — GoBD-konform mehrfach möglich.
        </p>`}

        <div class="receipt-action-grid">
          <button class="receipt-action-tile ${mailDisabled ? 'is-disabled' : ''}"
                  onclick="zBonActionSend()" ${mailDisabled ? 'disabled' : ''}>
            <div class="receipt-action-icon">✉</div>
            <div class="receipt-action-label">Z-Bon versenden</div>
            <div class="receipt-action-desc">Per Mail an den Steuerberater</div>
            ${mailHint}
          </button>

          <button class="receipt-action-tile" onclick="zBonActionPrint()">
            <div class="receipt-action-icon">🖨️</div>
            <div class="receipt-action-label">Z-Bon drucken</div>
            <div class="receipt-action-desc">Browser-Druckdialog (80&nbsp;mm-Bonlayout)</div>
          </button>

          ${isPastDay ? `
          <button class="receipt-action-tile" onclick="zBonActionPdf()">
            <div class="receipt-action-icon">📄</div>
            <div class="receipt-action-label">Z-Bon als PDF</div>
            <div class="receipt-action-desc">Gespeichertes PDF öffnen (Alt-Z-Bon lazy)</div>
          </button>` : ''}
        </div>

        <div class="receipt-action-foot">
          <button class="btn btn-ghost btn-sm" onclick="closeZBonReceiptActionModal()">Schließen</button>
        </div>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeZBonReceiptActionModal();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') { closeZBonReceiptActionModal(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
}

export function closeZBonReceiptActionModal() {
  const m = document.getElementById('zbon-action-modal');
  if (m) m.remove();
  _currentZBon = null;
}

/**
 * Merge-Helper: Z-Bon-Archiv-Eintrag aktualisieren oder anlegen.
 *
 * S29 KRITISCH-Fix: schreibt jetzt direkt in die `zbon_archive`-Tabelle.
 * Kein localStorage mehr. Wird von zBonActionSend/Print/Archive
 * await-ed — der Call-Site bekommt also Erfolg/Fehler synchron mit.
 */
async function upsertZBonArchiveEntry(dateIso, data, html, mutator) {
  const archive = loadZBonArchive();
  // Wir benutzen das Datum als Lookup-Key — entspricht der UNIQUE-Klausel
  // (restaurant_id, date) der zbon_archive-Tabelle. Das alte zBonNumber-
  // basierte Key-Schema war eine localStorage-Eigenheit.
  const key = dateIso;
  // S92: `html` wird vom Aufrufer als DB-autoritativer Snapshot übergeben
  // (buildStandaloneZBonHtmlFresh). Fallback auf Sync nur, falls nicht geliefert.
  if (!html) html = buildStandaloneZBonHtml(dateIso);
  const existing = archive[key] || {
    zBonNumber:        data.zBonNumber || '',
    date:              dateIso,
    count:             data.count || 0,
    countCorrections:  data.countCorrections || 0,
    countCancelled:    data.countCancelled || 0,
    countNoShow:       data.countNoShow || 0,
    grossCents:        data.grossCents || 0,
    netCents:          data.netCents || 0,
    tax7Cents:         data.tax7Cents || 0,
    tax19Cents:        data.tax19Cents || 0,
    byMethod:          data.byMethod || { cash: 0, card: 0, stripe: 0, unpaid: 0 },
    invoiceFirst:      data.invoiceFirst || null,
    invoiceLast:       data.invoiceLast || null,
    archivedAt:        null,
    sentAt:            null,
    sentTo:            null,
    printedAt:         null,
    html
  };
  // Snapshot aktualisieren — neueste HTML-Repräsentation und aktuelle Counts.
  existing.count            = data.count || 0;
  existing.countCorrections = data.countCorrections || 0;
  existing.countCancelled   = data.countCancelled || 0;
  existing.countNoShow      = data.countNoShow || 0;
  existing.grossCents       = data.grossCents || 0;
  existing.netCents         = data.netCents || 0;
  existing.tax7Cents        = data.tax7Cents || 0;
  existing.tax19Cents       = data.tax19Cents || 0;
  existing.byMethod         = data.byMethod || existing.byMethod;
  existing.invoiceFirst     = data.invoiceFirst || existing.invoiceFirst;
  existing.invoiceLast      = data.invoiceLast || existing.invoiceLast;
  existing.html             = html;
  mutator(existing);
  // Sicherstellen, dass `date` gesetzt ist — UNIQUE-Key der DB-Tabelle.
  existing.date = existing.date || dateIso;

  // S213-C2 — Z-Bon-PDF beim Archivieren EINMAL erzeugen und ablegen (analog
  // S213-A/bon_archive). Setzt existing.pdfPath VOR dem upsert, damit es im
  // Erst-INSERT mitkommt (INSERT für alle; ein späteres UPDATE wäre owner/admin).
  // Best-effort: blockiert den Tagesabschluss NIE (Fehler → html bleibt,
  // pdf_path NULL, Lazy-Nachzug im UI).
  await _ensureZBonPdf(existing);

  // S29 KRITISCH-Fix (Memory-Lesson gg-db-for-critical-data):
  // DB ist die einzige Persistenz. Upsert aktualisiert synchron den
  // In-Memory-Cache (_state.zbonArchive) bei Erfolg.
  if (!window.App || !window.App.storage || !window.App.storage.zbonArchive) {
    throw new Error('Z-Bon-Archiv-Storage nicht verfügbar.');
  }
  await window.App.storage.zbonArchive.upsert(existing);

  return existing;
}

/**
 * S213-C2 — Z-Bon-PDF erzeugen + in den Bucket bon-pdfs laden, pdf_path in den
 * (übergebenen) Eintrag schreiben. No-Op, wenn schon vorhanden oder der Adapter
 * die PDF-Methoden nicht kennt (Test-Umgebung). Best-effort, nie werfend.
 */
async function _ensureZBonPdf(existing) {
  try {
    const za = window.App && window.App.storage && window.App.storage.zbonArchive;
    if (!za || typeof za.uploadPdfBase64 !== 'function') return;
    if (!existing || existing.pdfPath || !existing.html || !existing.zBonNumber) return;
    const att = await _buildReceiptPdfAttachment(existing.html, `Z-Bon-${existing.zBonNumber}`);
    const base64 = att && att[0] && att[0].content;
    if (!base64) return;
    existing.pdfPath = await za.uploadPdfBase64(existing.zBonNumber, base64);
  } catch (e) {
    console.warn('[zbon] PDF-Persistenz fehlgeschlagen (nicht-fatal):', e);
  }
}

function rerenderDayClose() {
  if (typeof window.loadAndRenderDayClose === 'function') {
    window.loadAndRenderDayClose();
  }
}

export async function zBonActionSend() {
  if (!_currentZBon) return;
  const { dateIso } = _currentZBon;
  try {
    // S92: Versand-Beleg + Archiv-Snapshot frisch aus der DB (Single-Source).
    const data = await computeDayCloseReceiptFresh(dateIso);
    const html = await buildStandaloneZBonHtmlFresh(dateIso);

    // S213-C2 — Ein-Pfad: (1) ARCHIVIEREN ZUERST (idempotent) — erzeugt+lädt das
    // Z-Bon-PDF hoch + setzt pdf_path, damit die Edge es anhängen kann. Best-effort:
    // ein UPDATE einer bereits archivierten Zeile ist (Manager) RLS-gesperrt, das
    // PDF ist dann aber schon aus dem Erst-INSERT abgelegt → Enqueue läuft trotzdem.
    try {
      await upsertZBonArchiveEntry(dateIso, data, html, (e) => {
        if (!e.archivedAt) e.archivedAt = new Date().toISOString();
      });
    } catch (archiveErr) {
      console.warn('[zBonActionSend] Archivierung best-effort (pdf_path evtl. schon da):', archiveErr);
    }

    const payload = {
      zBonNumber: data.zBonNumber,
      dateIso,
      dateLabel:  fmtDateDE(dateIso),
      grossLabel: centsToEur(data.grossCents),
      count:      data.count,
      html
    };
    // (2) Server-seitig enqueuen (sendZBonReceiptEmail ist jetzt der Enqueue-
    // Wrapper: RPC enqueue_zbon_receipt_mail; Cron/Edge hängt das gespeicherte PDF an).
    const result = await sendZBonReceiptEmail(payload);
    if (result && result.success) {
      // Async ~60 s → „in Warteschlange". Der sent_at-Update bleibt best-effort
      // (zbon_archive-RLS: UPDATE nur owner/admin; Memory `gg-zbon-archive-rls`).
      showToast(`Z-Bon ${data.zBonNumber} wird versendet (Warteschlange).`, 'success');
      closeZBonReceiptActionModal();
      rerenderDayClose();
      try {
        await upsertZBonArchiveEntry(dateIso, data, html, (e) => {
          e.sentAt = new Date().toISOString();
          e.sentTo = result.to || (getState().settings || {}).accountantEmail || (getState().settings || {}).restaurantEmail || null;
        });
        rerenderDayClose();
      } catch (archiveErr) {
        console.warn('[zBonActionSend] sent_at-Update best-effort (Mail ist enqueued):', archiveErr);
      }
    } else if (result && result.skipped) {
      showToast('Versand übersprungen: ' + (result.reason || 'keine Empfänger-Mail'), 'info');
    } else {
      showToast('Versand fehlgeschlagen: ' + ((result && result.error) || 'unbekannt'), 'error');
    }
  } catch (e) {
    console.error('[zBonActionSend]', e);
    showToast('Versand fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/**
 * S213-C2 — pdf_path-Garantie für einen archivierten Z-Bon (Alt-Z-Bon-Lazy).
 * Ist das PDF schon abgelegt, nichts tun. Sonst aus dem archivierten `html`
 * erzeugen → hochladen → `setPdfPath` (owner/admin, RLS; best-effort). Der
 * Upload selbst geht für alle Authentifizierten (Storage-INSERT tenant-scoped),
 * nur der pdf_path-Zeiger-UPDATE ist owner/admin. Liefert Pfad oder null.
 */
async function _ensureZBonPdfPath(dateIso) {
  const za = window.App && window.App.storage && window.App.storage.zbonArchive;
  if (!za || typeof za.uploadPdfBase64 !== 'function') return null;
  const entry = (loadZBonArchive() || {})[dateIso];
  if (!entry || !entry.zBonNumber) return null;
  if (entry.pdfPath) return entry.pdfPath;
  // S240-HF7: `html` steckt nicht mehr im Listen-Hydrate → lazy nachladen.
  const html = (typeof za.getHtml === 'function')
    ? ((await za.getHtml(dateIso)) || '') : (entry.html || '');
  if (!html) return null;
  const att = await _buildReceiptPdfAttachment(html, `Z-Bon-${entry.zBonNumber}`);
  const base64 = att && att[0] && att[0].content;
  if (!base64) return null;
  const path = await za.uploadPdfBase64(entry.zBonNumber, base64);
  try { await za.setPdfPath(dateIso, path); }
  catch (e) { console.warn('[zbon] pdf_path-Backfill übersprungen (RLS/owner-only):', e); }
  return path;
}

/**
 * S213-C2 — „📄 PDF": den gespeicherten Z-Bon im In-App-Modal öffnen (gleiches
 * Bon-PDF-Modal wie im Bons-Tab, blob→iframe). Alt-Z-Bon ohne pdf_path → lazy.
 */
export async function zBonActionPdf(dateIsoArg) {
  const dateIso = dateIsoArg || (_currentZBon && _currentZBon.dateIso);
  if (!dateIso) return;
  try {
    const path = await _ensureZBonPdfPath(dateIso);
    if (!path) { showToast('Kein Z-Bon-PDF verfügbar.', 'error'); return; }
    const entry = (loadZBonArchive() || {})[dateIso] || {};
    const nr = entry.zBonNumber || '';
    if (typeof window.openBonPdfByPath === 'function') {
      window.openBonPdfByPath(path, `Z-Bon ${nr}`, `Z-Bon-${nr || dateIso}.pdf`);
    } else {
      showToast('Beleg-Anzeige nicht verfügbar.', 'error');
    }
  } catch (e) {
    console.error('[zBonActionPdf]', e);
    showToast('Z-Bon-PDF fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function zBonActionPrint() {
  if (!_currentZBon) return;
  const { dateIso } = _currentZBon;
  try {
    // S92: Druck + Archiv-Snapshot frisch aus der DB (Single-Source).
    // Body nur einmal aus der DB rendern, fürs Archiv-HTML wiederverwenden.
    const data = await computeDayCloseReceiptFresh(dateIso);
    const body = await renderDayCloseReceiptFresh(dateIso);
    const html = _wrapStandaloneZBonHtml(dateIso, body);
    printReceiptHtml(body);
    // S93-A: Druck ist ausgelöst → Erfolg ZUERST bestätigen. Archiv-Status-Update
    // (printed_at) ist best-effort (zbon_archive-RLS, s. zBonActionSend).
    showToast('Druckdialog geöffnet.', 'success');
    closeZBonReceiptActionModal();
    rerenderDayClose();
    try {
      await upsertZBonArchiveEntry(dateIso, data, html, (e) => {
        e.printedAt = new Date().toISOString();
      });
      rerenderDayClose();
    } catch (archiveErr) {
      console.warn('[zBonActionPrint] Archiv-Status-Update fehlgeschlagen (Druck ausgelöst):', archiveErr);
    }
  } catch (e) {
    console.error('[zBonActionPrint]', e);
    showToast('Druck fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function zBonActionArchive() {
  if (!_currentZBon) return;
  const { dateIso } = _currentZBon;
  try {
    // S92: Archiv-Snapshot frisch aus der DB (Single-Source).
    const data = await computeDayCloseReceiptFresh(dateIso);
    const html = await buildStandaloneZBonHtmlFresh(dateIso);
    const entry = await upsertZBonArchiveEntry(dateIso, data, html, (e) => {
      e.archivedAt = new Date().toISOString();
    });
    showToast(`Z-Bon ${entry.zBonNumber} archiviert.`, 'success');
    closeZBonReceiptActionModal();
    rerenderDayClose();
  } catch (e) {
    console.error('[zBonActionArchive]', e);
    showToast('Archivieren fehlgeschlagen: ' + (e.message || e), 'error');
  }
}
